<template>
    <div>
        <div v-if="agency.invitations.length > 0 && userPermissions.canAddAdvisors">
            <jet-section-border />

            <!-- Team Member Invitations -->
            <jet-action-section class="mt-10 sm:mt-0">
                <template #title> {{ $t("agencies.pendingTeamInvitations") }}</template>

                <template #description>
                    {{ $t("agencies.pendingTeamInvitationsDesc") }}
                </template>

                <!-- Pending Team Member Invitation List -->
                <template #content>
                    <div class="space-y-6">
                        <div
                            class="flex items-center justify-between"
                            v-for="invitation in agency.invitations"
                            :key="invitation.id"
                        >
                            <div class="text-gray-600">{{ invitation.email }}</div>

                            <div class="flex items-center">
                                <!-- Cancel Team Invitation -->
                                <button
                                    class="cursor-pointer ml-6 text-sm text-red-500 focus:outline-none"
                                    @click="cancelInvitation(invitation)"
                                    v-if="userPermissions.canRemoveAdvisors"
                                >
                                    {{ $t("actions.revoke") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </jet-action-section>
        </div>

        <div v-if="agency.advisors.length > 0">
            <jet-section-border />

            <!-- Manage Team Members -->
            <jet-action-section class="mt-10 sm:mt-0">
                <template #title> {{ $t("agencies.teamMembers") }}</template>

                <template #description>
                    {{ $t("agencies.teamMembersDescription") }}
                </template>

                <!-- Team Member List -->
                <template #content>
                    <div class="space-y-6">
                        <div
                            class="flex items-center justify-between"
                            v-for="advisor in agency.advisors"
                            :key="advisor.id"
                        >
                            <div class="flex items-center">
                                <div class="ml-4 leading-tight">
                                    {{ advisor.name }}
                                    <span v-if="advisor.is_primary_owner" class="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {{ $t('agencies.primaryOwner') }}
                                    </span>
                                    <br/>
                                    <span class="text-sm text-gray-400">{{ advisor.email }}</span>
                                </div>
                            </div>

                            <div class="flex items-center">
                                <!-- Manage Team Member Role -->
                                <button
                                    class="ml-2 text-sm text-gray-400 underline hover:text-gray-600 transition-colors"
                                    @click="manageRole(advisor)"
                                    v-if="canChangeRole(advisor)"
                                >
                                    {{ displayableRole(advisor.membership.role) }}
                                </button>

                                <div
                                    class="ml-2 text-sm text-gray-400"
                                    v-else-if="availableRoles.length"
                                >
                                    {{ displayableRole(advisor.membership.role) }}
                                </div>

                                <!-- Leave Agency -->
                                <button
                                    class="cursor-pointer ml-6 text-sm text-red-500"
                                    @click="confirmLeavingTeam"
                                    v-if="canLeave(advisor)"
                                >
                                    {{ $t("actions.leave") }}
                                </button>

                                <!-- Remove Team Member -->
                                <button
                                    class="cursor-pointer ml-6 text-sm text-red-500"
                                    @click="confirmAdvisorRemoval(advisor)"
                                    v-if="canRemove(advisor)"
                                >
                                    {{ $t("actions.remove") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </jet-action-section>
        </div>

        <!-- Role Management Modal -->
        <jet-dialog-modal
            :show="currentlyManagingRole"
            @close="currentlyManagingRole = false"
        >
            <template #title>{{ $t("agencies.manageRole") }}</template>

            <template #content>
                <div v-if="managingRoleFor">
                    <jet-input-error :message="updateRoleForm.errors.role" class="mb-2" />
                    <div
                        class="relative z-0 mt-1 border border-gray-200 rounded-lg cursor-pointer"
                    >
                        <button
                            type="button"
                            class="relative px-4 py-3 inline-flex w-full rounded-lg focus:z-10 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200"
                            :class="{
                                'border-t border-gray-200 rounded-t-none': i > 0,
                                'rounded-b-none':
                                    i !== Object.keys(assignableRoles).length - 1,
                            }"
                            @click="updateRoleForm.role = role.key"
                            v-for="(role, i) in assignableRoles"
                            :key="role.key"
                        >
                            <div
                                :class="{
                                    'opacity-50':
                                        updateRoleForm.role &&
                                        updateRoleForm.role !== role.key,
                                }"
                            >
                                <!-- Role Name -->
                                <div class="flex items-center">
                                    <div
                                        class="text-sm text-gray-600"
                                        :class="{
                                            'font-semibold':
                                                updateRoleForm.role === role.key,
                                        }"
                                    >
                                        {{ role.name }}
                                    </div>

                                    <svg
                                        v-if="updateRoleForm.role === role.key"
                                        class="ml-2 h-5 w-5 text-green-400"
                                        fill="none"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        ></path>
                                    </svg>
                                </div>

                                <!-- Role Description -->
                                <div class="mt-2 text-xs text-gray-600">
                                    {{ role.description }}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </template>

            <template #footer>
                <jet-secondary-button @click="currentlyManagingRole = false">
                    {{ $t("actions.cancel") }}
                </jet-secondary-button>

                <primary-button
                    class="ml-2"
                    @click="updateRole"
                    :class="{ 'opacity-25': updateRoleForm.processing }"
                    :disabled="updateRoleForm.processing"
                >
                    {{ $t("actions.save") }}
                </primary-button>
            </template>
        </jet-dialog-modal>

        <!-- Leave Team Confirmation Modal -->
        <dialog-modal
            :show="confirmingLeavingTeam"
            @close="confirmingLeavingTeam = false"
        >
            <template #title>{{ $t("agencies.leaveTeam") }}</template>

            <template #content>
                {{ $t("agencies.confirmLeaveTeam") }}
            </template>

            <template #footer>
                <secondary-button @click="confirmingLeavingTeam = false">
                    {{ $t("actions.cancel") }}
                </secondary-button>

                <primary-button
                    class="ml-2"
                    @click="leaveAgency()"
                    :class="{ 'opacity-25': leaveAgencyForm.processing }"
                    :disabled="leaveAgencyForm.processing"
                >
                    {{ $t("actions.leave") }}
                </primary-button>
            </template>
        </dialog-modal>

        <!-- Remove Team Member Confirmation Modal -->
        <dialog-modal :show="memberBeingRemoved" @close="memberBeingRemoved = null">
            <template #title>{{ $t("agencies.removeTeamMember") }}</template>

            <template #content>
                {{ $t("agencies.confirmRemoveTeamMember") }}
            </template>

            <template #footer>
                <secondary-button @click="memberBeingRemoved = null">
                    {{ $t("actions.cancel") }}
                </secondary-button>

                <primary-button
                    class="ml-2"
                    @click="removeAdvisor"
                    :class="{ 'opacity-25': removeMemberForm.processing }"
                    :disabled="removeMemberForm.processing"
                >
                    {{ $t("actions.remove") }}
                </primary-button>
            </template>
        </dialog-modal>

        <!-- Primary Owner Transfer Modal -->
        <dialog-modal :show="confirmingPrimaryOwnerTransfer" @close="confirmingPrimaryOwnerTransfer = false">
            <template #title>{{ $t('agencies.transferPrimaryOwnership') }}</template>

            <template #content>
                <div class="mb-4 text-sm text-gray-600">
                    {{ $t('agencies.transferPrimaryOwnershipDesc') }}
                </div>

                <div class="mt-4 space-y-4 max-h-60 overflow-y-auto">
                    <div
                        v-for="advisor in otherOwners"
                        :key="advisor.id"
                        class="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
                        @click="transferOwnership(advisor)"
                    >
                        <div class="flex items-center">
                            <div class="ml-4">
                                <div class="text-sm font-medium text-gray-900">{{ advisor.name }}</div>
                                <div class="text-sm text-gray-500">{{ advisor.email }}</div>
                            </div>
                        </div>
                        <div class="text-minplan-blue">
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    <div v-if="otherOwners.length === 0" class="text-center py-4 text-gray-500">
                        No other owners available. Please promote another member to owner first.
                    </div>
                </div>
            </template>

            <template #footer>
                <secondary-button @click="confirmingPrimaryOwnerTransfer = false">
                    {{ $t("actions.cancel") }}
                </secondary-button>
            </template>
        </dialog-modal>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import JetActionMessage from "@/Jetstream/ActionMessage.vue";
import JetActionSection from "@/Jetstream/ActionSection.vue";
import JetConfirmationModal from "@/Jetstream/ConfirmationModal.vue";
import JetDangerButton from "@/Jetstream/DangerButton.vue";
import JetDialogModal from "@/Jetstream/DialogModal.vue";
import JetFormSection from "@/Jetstream/FormSection.vue";
import JetInput from "@/Jetstream/Input.vue";
import JetInputError from "@/Jetstream/InputError.vue";
import JetLabel from "@/Jetstream/Label.vue";
import JetSecondaryButton from "@/Jetstream/SecondaryButton.vue";
import JetSectionBorder from "@/Jetstream/SectionBorder.vue";
import DialogModal from "@/Jetstream/DialogModal";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";

export default defineComponent({
    components: {
        JetActionMessage,
        JetActionSection,
        PrimaryButton,
        SecondaryButton,
        DialogModal,
        JetConfirmationModal,
        JetDangerButton,
        JetDialogModal,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetSectionBorder,
    },

    props: ["agency", "availableRoles", "userPermissions", "links"],

    data() {
        return {
            updateRoleForm: this.$inertia.form({
                role: null,
            }),

            leaveAgencyForm: this.$inertia.form(),
            removeMemberForm: this.$inertia.form(),

            currentlyManagingRole: false,
            managingRoleFor: null,
            confirmingLeavingTeam: false,
            confirmingPrimaryOwnerTransfer: false,
            memberBeingRemoved: null,
        };
    },

    methods: {
        cancelInvitation(invitation) {
            this.$inertia.delete(this.route(this.links.clearInvite, invitation), {
                preserveScroll: true,
                onSuccess: () =>
                    this.$notify({
                        group: "success",
                        title: "actions.success",
                        text: "agencies.inviteDeleted",
                    }),
            });
        },

        manageRole(advisor) {
            this.managingRoleFor = advisor;
            this.updateRoleForm.role = advisor.membership.role;
            this.currentlyManagingRole = true;
        },

        updateRole() {
            this.updateRoleForm.put(
                this.route(this.links.updateAdvisor, [
                    this.agency,
                    this.managingRoleFor.uuid,
                ]),
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.currentlyManagingRole = false;
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "agencies.roleUpdated",
                        });
                    },
                }
            );
        },

        confirmLeavingTeam() {
            const advisor = this.agency.advisors.find(a => a.uuid === this.$page.props.user.advisor_uuid);

            if (this.isOwner(advisor) && this.agency.owner_count <= 1) {
                this.$notify({
                    group: "error",
                    title: this.$t("actions.error"),
                    text: this.$t("agencies.makeSomeoneElsePrimaryOwner"),
                });
                return;
            }

            if (advisor && advisor.is_primary_owner) {
                this.$notify({
                    group: "error",
                    title: this.$t("actions.error"),
                    text: this.$t("agencies.makeSomeoneElsePrimaryOwner"),
                });
                this.confirmingPrimaryOwnerTransfer = true;
                return;
            }
            this.confirmingLeavingTeam = true;
        },

        transferOwnership(advisor) {
            this.$inertia.put(this.route(this.links.transferOwnership, [this.agency.uuid, advisor.uuid]), {}, {
                preserveScroll: true,
                onSuccess: () => {
                    this.confirmingPrimaryOwnerTransfer = false;
                    this.$notify({
                        group: "success",
                        title: "actions.success",
                        text: "agencies.ownershipTransferred",
                    });
                }
            });
        },

        leaveAgency() {
            this.leaveAgencyForm.delete(
                this.route(this.links.leave, [
                    this.agency,
                    this.$page.props.user.advisor_uuid,
                ])
            );
        },

        confirmAdvisorRemoval(advisor) {
            if (!this.canRemove(advisor)) {
                if (this.isOwner(advisor)) {
                    this.$notify({
                        group: "error",
                        title: this.$t("actions.error"),
                        text: "Only superadmins can remove owners, and at least one owner must remain.",
                    });
                }
                return;
            }
            this.memberBeingRemoved = advisor;
        },

        removeAdvisor() {
            this.removeMemberForm.delete(
                this.route(this.links.remove, [
                    this.agency,
                    this.memberBeingRemoved.uuid,
                ]),
                {
                    errorBag: "removeMember",
                    preserveScroll: true,
                    preserveState: true,
                    onSuccess: () => {
                        this.memberBeingRemoved = null;
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "agencies.advisorRemoved",
                        });
                    },
                }
            );
        },

        displayableRole(role) {
            return this.availableRoles.find((r) => r.key === role).name;
        },

        isOwner(advisor) {
            return (
                advisor.membership.role === "owner" ||
                advisor.id === this.agency.owner_id
            );
        },

        canChangeRole(advisor) {
            const isOwner = this.isOwner(advisor);
            const isSuperAdmin = this.$page.props.isSuperAdmin;
            const currentUserIsOwner = this.$page.props.user.currentAgencyRole === 'owner';

            // Cannot change role if it's the last owner
            if (isOwner && this.agency.owner_count <= 1) {
                return false;
            }

            // Owners can change roles of non-owners.
            // Superadmins can change roles of everyone (including owners).
            if (isSuperAdmin) return true;
            if (currentUserIsOwner && !isOwner) return true;

            return false;
        },

        canRemove(advisor) {
            const isOwner = this.isOwner(advisor);
            const isSuperAdmin = this.$page.props.isSuperAdmin;

            // Owners cannot be removed by anyone except superadmins.
            if (isOwner) {
                if (!isSuperAdmin) return false;
                // Cannot remove the last owner
                return this.agency.owner_count > 1;
            }

            // Otherwise, anyone with removal permissions can remove.
            return this.userPermissions.canRemoveAdvisors;
        },

        canLeave(advisor) {
            return (
                this.userPermissions.canLeaveAgency &&
                advisor.uuid === this.$page.props.user.advisor_uuid
            );
        },
    },

    computed: {
        assignableRoles() {
            const isSuperAdmin = this.$page.props.isSuperAdmin;
            const currentUserIsOwner = this.$page.props.user.currentAgencyRole === 'owner';
            const canAddOwner = this.userPermissions.canAddOwner;
            const isTargetAlreadyOwner = this.managingRoleFor && this.isOwner(this.managingRoleFor);

            let roles = this.availableRoles.filter(r => r.key !== 'advisor');

            if (isSuperAdmin || currentUserIsOwner) {
                // If the limit is reached, and the target is NOT already an owner, hide "owner" option
                if (!canAddOwner && !isTargetAlreadyOwner) {
                    return roles.filter(r => r.key !== 'owner');
                }
                return roles;
            }
            return roles.filter((r) => r.key !== "owner");
        },

        otherOwners() {
            return this.agency.advisors.filter(a => this.isOwner(a) && !a.is_primary_owner);
        }
    }
});
</script>
